import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Youtube, MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/data/site";
import { subscribeNewsletter } from "@/lib/api";

const cols = [
  { title: "Company", links: [["About", "#about"], ["Services", "#services"], ["Our Network", "#network"], ["Insights", "/insights"], ["Contact", "#contact"]] },
  { title: "Solutions", links: [["Institution Solutions", "#institutions"], ["Student Solutions", "#students"], ["Website & Technology", "#services"], ["Digital Marketing", "#services"], ["Calling Engine", "#services"]] },
  { title: "Legal", links: [["Privacy Policy", "#"], ["Terms & Conditions", "#"], ["Cookie Preferences", "#"]] },
];

export function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed! Watch your inbox for A3 insights.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative border-t border-white/10 bg-ink-1" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src="/a3-logo-mark.png" alt="A3 Global logo" className="h-12 w-auto" />
              <div>
                <div className="font-display text-2xl font-bold text-white">
                  A3 <span className="text-gradient">GLOBAL</span>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
                  Marketing &amp; Education
                </p>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/55">
              Education. Technology. Marketing. Growth. A complete education growth partner for MBA &amp; PGDM institutions across India.
            </p>
            <div className="mt-6 space-y-3 text-sm text-white/60">
              <a href={CONTACT.mapsUrl} target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-brand-cyan">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-cyan" /> {CONTACT.address}
              </a>
              <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-3 hover:text-brand-cyan">
                <Phone className="h-4 w-4 text-brand-cyan" /> {CONTACT.phoneDisplay}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-brand-cyan">
                <Mail className="h-4 w-4 text-brand-cyan" /> {CONTACT.email}
              </a>
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">{col.title}</h4>
              <ul className="mt-4 space-y-2.5">
                {col.links.map(([label, href]) => (
                  <li key={label}>
                    {href.startsWith("/") ? (
                      <Link to={href} className="text-sm text-white/60 transition-colors hover:text-brand-cyan">{label}</Link>
                    ) : (
                      <a href={href} className="text-sm text-white/60 transition-colors hover:text-brand-cyan">{label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Newsletter</h4>
            <p className="mt-4 text-sm text-white/55">Education growth insights, monthly.</p>
            <form onSubmit={subscribe} className="mt-4" data-testid="newsletter-form">
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1 pl-4">
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  placeholder="Email"
                  className="w-full bg-transparent text-sm text-white outline-none placeholder:text-white/35"
                  data-testid="newsletter-email"
                />
                <button disabled={busy} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan text-[#08120A]" data-testid="newsletter-submit">
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} A3 Global Marketing &amp; Education. You own the institution. We build, market, operate &amp; grow it.</p>
          <div className="flex items-center gap-3">
            {[Linkedin, Instagram, Facebook, Youtube].map((Icon, i) => (
              <a key={i} href="#" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-brand-cyan/50 hover:text-brand-cyan">
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
